import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Search, MapPin } from "lucide-react";

function tableRadius(seats) { return Math.max(30, Math.min(64, 24 + seats * 2.4)); }
function rectSize(seats) { return { w: Math.max(80, Math.min(280, 56 + seats * 9)), h: 46 }; }

export default function PublicTablePlanPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/public/table-plan/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [token]);

  const match = useMemo(() => {
    if (!data || !q.trim()) return null;
    return data.guests.find((g) => g.name.toLowerCase().includes(q.trim().toLowerCase()));
  }, [data, q]);

  const myTable = match?.table_id ? data.tables.find((t) => t.id === match.table_id) : null;
  const tablemates = myTable ? data.guests.filter((g) => g.table_id === myTable.id) : [];
  const guestsByTable = (tid) => (data?.guests || []).filter((g) => g.table_id === tid);

  if (loading) return <div className="z-container" style={{ textAlign: "center", paddingTop: 60 }}><span className="z-loader z-loader-dark" /></div>;
  if (err || !data) return (
    <div className="z-container" style={{ textAlign: "center", paddingTop: 60 }} data-testid="public-table-plan-notfound">
      <h1 className="z-page-title">Plan introuvable</h1>
      <p className="z-page-sub">Ce plan de table n'est pas disponible ou n'a pas été publié.</p>
    </div>
  );

  return (
    <div className="z-container" data-testid="public-table-plan-page" style={{ maxWidth: 900 }}>
      <div style={{ textAlign: "center", marginBottom: 18 }}><Logo to={null} size={26} /></div>
      <h1 className="z-page-title" style={{ textAlign: "center" }}>Plan de table</h1>
      <p className="z-page-sub" style={{ textAlign: "center" }}>{data.event_title}</p>

      <div style={{ position: "relative", maxWidth: 460, margin: "0 auto 20px" }}>
        <Search size={18} style={{ position: "absolute", left: 14, top: 14, color: "var(--z-muted)" }} />
        <input className="z-input" style={{ paddingLeft: 42 }} placeholder="Tapez votre nom pour trouver votre table…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="public-guest-search" />
      </div>

      {q.trim() && (
        match ? (
          myTable ? (
            <div className="z-card" style={{ padding: 20, textAlign: "center", marginBottom: 22, borderTop: `4px solid ${myTable.color}` }} data-testid="public-search-result">
              <div style={{ color: "var(--z-teal)", display: "flex", justifyContent: "center", marginBottom: 6 }}><MapPin size={28} /></div>
              <p style={{ margin: 0 }}>Bonjour <strong>{match.name}</strong>, vous êtes à la</p>
              <div style={{ fontSize: 26, fontWeight: 800, color: "var(--z-teal)", margin: "4px 0" }} data-testid="public-my-table">{myTable.label}</div>
              {tablemates.length > 1 && (
                <p style={{ fontSize: 13, color: "var(--z-muted)", margin: 0 }}>Avec : {tablemates.filter((g) => g.id !== match.id).map((g) => g.name).join(", ")}</p>
              )}
            </div>
          ) : (
            <div className="z-card" style={{ padding: 18, textAlign: "center", marginBottom: 22 }} data-testid="public-search-result">
              <strong>{match.name}</strong> n'a pas encore de table attribuée.
            </div>
          )
        ) : (
          <div className="z-card" style={{ padding: 18, textAlign: "center", marginBottom: 22, color: "var(--z-muted)" }} data-testid="public-search-noresult">
            Aucun invité trouvé pour « {q} ».
          </div>
        )
      )}

      {/* Visual plan */}
      {data.tables.length > 0 && (
        <div className="z-card" style={{ padding: 12, overflow: "auto", marginBottom: 20 }}>
          <svg viewBox={`0 0 ${data.width} ${data.height}`} style={{ width: "100%", minWidth: 560 }}>
            {data.tables.map((t) => {
              const hl = myTable && myTable.id === t.id;
              return (
                <g key={t.id}>
                  {t.shape === "round" ? (
                    <circle cx={t.x} cy={t.y} r={tableRadius(t.seats)} fill={`${t.color}${hl ? "55" : "22"}`} stroke={hl ? "#111" : t.color} strokeWidth={hl ? 4 : 2} />
                  ) : (() => { const { w, h } = rectSize(t.seats); return (
                    <rect x={t.x - w / 2} y={t.y - h / 2} width={w} height={h} rx={8} fill={`${t.color}${hl ? "55" : "22"}`} stroke={hl ? "#111" : t.color} strokeWidth={hl ? 4 : 2} />
                  ); })()}
                  <text x={t.x} y={t.y + 4} textAnchor="middle" fontSize="13" fontWeight="700" fill="#111">{t.label}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {/* List */}
      <div className="z-card" style={{ padding: 16 }} data-testid="public-table-list">
        {data.tables.map((t) => (
          <div key={t.id} style={{ marginBottom: 12, borderLeft: `4px solid ${t.color}`, paddingLeft: 12 }}>
            <div style={{ fontWeight: 700 }}>{t.label}</div>
            <div style={{ fontSize: 14, color: "var(--z-muted)" }}>
              {guestsByTable(t.id).map((g) => g.name).join(", ") || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
